import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ContentManagementInterface, { ContentItem } from '../../models/ContentManagementInterface';
import type FundOpportunity from '../../models/FundOpportunity';
import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import BackendService from '../../services/admin/BackendService';
import { restoreCardsAdjust } from '../admin/newContentManagement/cardsAdjustUtils';
import { AUTOSAVE_DELAY_MS } from '../admin/newContentManagement/newContentManagementEditorConstants';
import { appendContentSections } from '../admin/newContentManagement/newContentManagementEditorUtils';
import type {
  PageMode,
  SaveStatus,
} from '../admin/newContentManagement/newContentManagementEditorTypes';
import type { ImageCardsCropMeta } from '../admin/newContentManagement/useImageCardsSplit';
import { createNewBlock, type FundUpdateBlockType } from '../fundUpdateCms/fundUpdateCmsBlockUtils';
import type { SimplifiedFundUpdateEditorHandle } from './SimplifiedFundUpdateEditor.types';

function createContent(update: AdminFundUpdateEntry, fundOpportunity: FundOpportunity) {
  return {
    name: `${fundOpportunity.name ?? 'Fund'} update`,
    relatedObject: 'FundUpdate',
    relatedObjectId: update.externalId,
    content: (update.content ?? []) as ContentItem[],
    updateCard: update.updateCard ?? undefined,
  } satisfies ContentManagementInterface;
}

function useDraftContent(
  update: AdminFundUpdateEntry,
  fundOpportunity: FundOpportunity,
  backendService: React.MutableRefObject<BackendService>,
  setCardsAdjustByBlockId: (
    updater: (prev: Record<string, ImageCardsCropMeta>) => Record<string, ImageCardsCropMeta>,
  ) => void,
) {
  const [content, setContent] = useState<ContentManagementInterface | null>(null);
  const [loading, setLoading] = useState(true);
  const latestContent = useRef<ContentManagementInterface | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    backendService.current
      .getFundOpportunityContent('FundUpdate', update.externalId, true)
      .then((raw) => {
        if (cancelled) return;
        if (raw) {
          const next = JSON.parse(raw) as ContentManagementInterface;
          setContent(next);
          latestContent.current = next;
          restoreCardsAdjust(
            backendService.current,
            'FundUpdate',
            update.externalId,
            next,
            setCardsAdjustByBlockId,
          );
        } else {
          const next = createContent(update, fundOpportunity);
          setContent(next);
          latestContent.current = next;
        }
      })
      .catch((err) => {
        console.error('[SimplifiedFundUpdateEditor] failed to load draft content:', err);
        const fallback = createContent(update, fundOpportunity);
        setContent(fallback);
        latestContent.current = fallback;
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Only reload when the update being edited actually changes (by id) —
    // `update` is a new object reference whenever the caller syncs live edits
    // back into its own state (see SimplifiedFundUpdateEditor's onContentChange),
    // and depending on the whole object here would re-trigger this fetch every
    // time that happens, in an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendService, fundOpportunity, update.externalId]);

  return { content, setContent, loading, latestContent };
}

function useAutosave(
  updateId: string,
  backendService: React.MutableRefObject<BackendService>,
  latestContent: React.MutableRefObject<ContentManagementInterface | null>,
  setContent: React.Dispatch<React.SetStateAction<ContentManagementInterface | null>>,
) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistContent = useCallback(
    async (updated: ContentManagementInterface) => {
      setSaveStatus('saving');
      await backendService.current.uploadContentManagementUpdate('FundUpdate', updateId, {
        ...updated,
        relatedObject: 'FundUpdate',
        relatedObjectId: updateId,
      });
      setSaveStatus('saved');
    },
    [backendService, updateId],
  );

  const scheduleAutoSave = useCallback(
    (updated: ContentManagementInterface) => {
      latestContent.current = updated;
      setSaveStatus('pending');
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        if (latestContent.current) void persistContent(latestContent.current);
      }, AUTOSAVE_DELAY_MS);
    },
    [latestContent, persistContent],
  );

  const updateContent = useCallback(
    (updated: ContentManagementInterface) => {
      setContent(updated);
      scheduleAutoSave(updated);
    },
    [scheduleAutoSave, setContent],
  );

  const saveNow = useCallback(async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (latestContent.current) await persistContent(latestContent.current);
  }, [latestContent, persistContent]);

  return { saveStatus, scheduleAutoSave, updateContent, saveNow };
}

function useBlockActions(
  content: ContentManagementInterface | null,
  updateId: string,
  updateContent: (updated: ContentManagementInterface) => void,
  backendService: React.MutableRefObject<BackendService>,
  setCardsAdjustByBlockId: (
    updater: (prev: Record<string, ImageCardsCropMeta>) => Record<string, ImageCardsCropMeta>,
  ) => void,
) {
  const updateBlock = (index: number, updatedBlock: ContentItem) => {
    if (!content) return;
    const blocks = [...content.content];
    blocks[index] = updatedBlock;
    updateContent({ ...content, content: blocks });
  };

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    if (!content) return;
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= content.content.length) return;
    const blocks = [...content.content];
    [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
    updateContent({ ...content, content: blocks });
  };

  const removeBlock = (index: number) => {
    if (content)
      updateContent({ ...content, content: content.content.filter((_, i) => i !== index) });
  };

  const addBlock = (index: number, type: ContentItem['type'], where: 'before' | 'after') => {
    if (!content || type === 'ChartData') return;
    const blocks = [...content.content];
    const block = createNewBlock(type as FundUpdateBlockType, updateId) as ContentItem;
    blocks.splice(where === 'before' ? index : index + 1, 0, block);
    updateContent({ ...content, content: blocks });
  };

  const insertSections = (index: number, sections: ContentItem[]) => {
    if (!content || sections.length === 0) return;
    const blocks = [...content.content];
    blocks.splice(index, 1, ...sections);
    updateContent({ ...content, content: blocks });
  };

  const appendImportedSections = (
    sections: ContentItem[],
    cropMeta?: ImageCardsCropMeta | null,
    atIndex?: number,
  ) => {
    if (!content || sections.length === 0) return;
    const next = appendContentSections(content, sections, atIndex);
    updateContent(next.content);
    // An unscoped (not block-targeted) import that detected cards: prime "Adjust crops" on the
    // one block it created — cropMeta is only ever set for a single-page import, which always
    // produces exactly one ImageCarousel block (see importDocumentSelection's backend contract).
    if (cropMeta?.source && sections.length === 1 && sections[0].type === 'ImageCarousel') {
      const blockId = next.insertedIds[0];
      setCardsAdjustByBlockId((prev) => ({ ...prev, [blockId]: cropMeta }));
      backendService.current
        .saveImageCardCropSource({
          modelType: 'FundUpdate',
          modelId: updateId,
          contentBlockId: blockId,
          source: cropMeta.source,
          boxes: cropMeta.boxes,
        })
        .catch((err) => console.warn('Could not save the crop source for Adjust crops:', err));
    }
  };

  return { updateBlock, moveBlock, removeBlock, addBlock, insertSections, appendImportedSections };
}

function useEditorHandle(
  ref: React.ForwardedRef<SimplifiedFundUpdateEditorHandle>,
  saveStatus: SaveStatus,
  saveNow: () => Promise<void>,
  latestContent: React.MutableRefObject<ContentManagementInterface | null>,
  setContent: React.Dispatch<React.SetStateAction<ContentManagementInterface | null>>,
  scheduleAutoSave: (updated: ContentManagementInterface) => void,
  setPageMode: (mode: PageMode) => void,
) {
  const [publishBlocked, setPublishBlocked] = useState(false);
  useImperativeHandle(
    ref,
    () => ({
      state: { requireSave: saveStatus === 'pending' || saveStatus === 'error' },
      saveContent: saveNow,
      setUpdateCard: (card) => {
        if (!latestContent.current) return;
        const next = { ...latestContent.current };
        if (card) next.updateCard = card;
        else delete next.updateCard;
        setContent(next);
        scheduleAutoSave(next);
      },
      enterEditMode: () => setPageMode('edit'),
      setPublishBlocked,
    }),
    [latestContent, saveNow, saveStatus, scheduleAutoSave, setContent, setPageMode],
  );
  return publishBlocked;
}

export function useSimplifiedFundUpdateEditor(
  update: AdminFundUpdateEntry,
  fundOpportunity: FundOpportunity,
  ref: React.ForwardedRef<SimplifiedFundUpdateEditorHandle>,
  onBusyChange?: (busy: boolean) => void,
) {
  const [pageMode, setPageMode] = useState<PageMode>('edit');
  const backendService = useRef(new BackendService());
  const toolbarRef = useRef<HTMLDivElement>(null);
  // ImageCarousel blocks that should prime their own "Adjust crops" on mount, keyed by content
  // block id — populated both by a fresh unscoped Documents import and, on load, by restoring any
  // still-live crop source from a previous session.
  const [cardsAdjustByBlockId, setCardsAdjustByBlockId] = useState<
    Record<string, ImageCardsCropMeta>
  >({});
  // Explicit open/closed overrides for a StandardHeaderText block's Footnotes panel, keyed by
  // content block id — see `isFootnotesOpen`.
  const [footnotesOpenByBlockId, setFootnotesOpenByBlockId] = useState<Record<string, boolean>>({});
  const draft = useDraftContent(update, fundOpportunity, backendService, setCardsAdjustByBlockId);
  const autosave = useAutosave(
    update.externalId,
    backendService,
    draft.latestContent,
    draft.setContent,
  );
  const publishBlocked = useEditorHandle(
    ref,
    autosave.saveStatus,
    autosave.saveNow,
    draft.latestContent,
    draft.setContent,
    autosave.scheduleAutoSave,
    setPageMode,
  );
  const blockActions = useBlockActions(
    draft.content,
    update.externalId,
    autosave.updateContent,
    backendService,
    setCardsAdjustByBlockId,
  );

  useEffect(
    () => onBusyChange?.(autosave.saveStatus === 'saving'),
    [autosave.saveStatus, onBusyChange],
  );

  return {
    content: draft.content,
    loading: draft.loading,
    saveStatus: publishBlocked ? ('pending' as SaveStatus) : autosave.saveStatus,
    pageMode,
    setPageMode,
    toolbarRef,
    backendService: backendService.current,
    saveNow: autosave.saveNow,
    cardsAdjustByBlockId,
    clearCardsAdjustForBlock: (blockId: string) =>
      setCardsAdjustByBlockId((prev) => {
        if (!(blockId in prev)) return prev;
        const next = { ...prev };
        delete next[blockId];
        return next;
      }),
    footnotesOpenByBlockId,
    toggleFootnotes: (blockId: string, currentlyOpen: boolean) =>
      setFootnotesOpenByBlockId((prev) => ({ ...prev, [blockId]: !currentlyOpen })),
    ...blockActions,
  };
}
